import React, {useEffect, useRef, useState} from 'react'
import ReactDOM from 'react-dom'
import {Button} from '@components/ui/button/Button.tsx'
import {XCloseIcon} from '@components/ui/icon/Icon.tsx'
import {
    StyledBrowserEmbeddedContent,
    StyledBrowserEmbeddedContentParagraph,
    StyledBrowserEmbeddedOverlay
} from '@/features/measures/components/browser-embedded/style.ts'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle
} from '@components/ui/modal-atoms/ModalAtoms.tsx'
import {useTranslation} from 'react-i18next'
import {transformXml} from '@/utilities/helpers'

interface BrowserEmbeddedProps {
    htmlContent: string
    onClose: () => void
    isOpen: boolean
    isOmissis?: boolean
    measureType?: string
}

export const BrowserEmbedded: React.FC<BrowserEmbeddedProps> = ({
    isOpen,
    onClose,
    htmlContent,
    isOmissis = false,
    measureType = 'default'
}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const [showWarning, setShowWarning] = useState(false)
    const [showContent, setShowContent] = useState(false)
    const {t} = useTranslation()

    useEffect(() => {
        if (isOpen) {
            if (isOmissis && !showContent) {
                setShowWarning(true)
            } else {
                setShowContent(true)
            }
        } else {
            setShowContent(false)
            setShowWarning(false)
        }
    }, [isOpen, isOmissis])

    useEffect(() => {
        if (showContent && iframeRef.current) {
            document.body.style.overflow = 'hidden'

            const iframe = iframeRef.current
            const doc = iframe.contentDocument || iframe.contentWindow?.document
            if (doc) {
                doc.open()

                const processContent = async () => {
                    let processedContent = htmlContent

                    if (htmlContent.includes('<?xml version="1.0"')) {
                        processedContent = await transformXml(htmlContent, measureType)
                    }

                    let modifiedContent = processedContent.replace(
                        /body\.corpo\s*{\s*margin:\s*2cm\s*1\.5cm\s*1cm\s*1\.5cm/g,
                        'body.corpo { margin: 10px'
                    )

                    // Verifica se il testo di "calce" è presente, altrimenti aggiungilo
                    if (isOmissis && !modifiedContent.includes('<p class="calce">')) {
                        modifiedContent += `<p class="calce">In caso di diffusione omettere le generalità e gli altri dati identificativi dei soggetti interessati nei termini indicati.</p>`
                    }

                    const content = `
                        <!DOCTYPE html>
                        <html>
                            <head>
                                <meta charset="utf-8">
                                <meta name="viewport" content="width=device-width, initial-scale=1">
                                <base href="/">
                                <style>
                                    body { 
                                        margin: 0; 
                                        padding: 16px; 
                                        font-family: system-ui;
                                        display: flex;
                                        flex-direction: column;
                                        align-items: center;
                                    }
                                    #content {
                                        overflow-x: hidden;
                                        width: 100%;
                                        box-sizing: border-box;
                                    }
                                    img { 
                                        max-width: 100%; 
                                        height: auto;
                                        display: block;
                                        margin: 20px auto;
                                    }
                                    .error-message {
                                        color: red;
                                        display: none;
                                    }
                                    body.corpo {
                                        margin: 10px;
                                    }
                                    @media (max-width: 768px) {
                                        .registri {
                                            margin-top: 30px;
                                        }
                                    }
                                </style>
                            </head>
                            <body>
                                <div id="content">
                                    ${modifiedContent}
                                </div>
                                <div class="error-message" id="error-message"></div>
                                <script>
                                    document.addEventListener('DOMContentLoaded', function() {
                                        const images = document.getElementsByTagName('img');
                                        for(let img of images) {
                                            img.onerror = function() {
                                                console.error('Failed to load image:', img.src);
                                                const errorDiv = document.getElementById('error-message');
                                                errorDiv.style.display = 'block';
                                                errorDiv.textContent = 'Failed to load image: ' + img.src;
                                            };
                                            img.onload = function() {
                                                console.log('Image loaded successfully:', img.src);
                                            };
                                        }
                                    });
                                </script>
                            </body>
                        </html>
                    `
                    doc.write(content)
                    doc.close()
                }

                processContent()
            }
        }

        return () => {
            document.body.style.overflow = 'auto'
        }
    }, [showContent, htmlContent, isOmissis, measureType])

    const handleConfirmView = () => {
        setShowWarning(false)
        setShowContent(true)
    }

    const handleCancel = () => {
        setShowWarning(false)
        onClose()
    }

    if (!isOpen) return null

    return ReactDOM.createPortal(
        <>
            {showWarning && (
                <>
                    <ModalOverlay />
                    <Modal width="400px">
                        <ModalHeader>
                            <ModalTitle>{t('measures:omissis_warning:title')}</ModalTitle>
                        </ModalHeader>
                        <ModalBody>
                            <StyledBrowserEmbeddedContentParagraph>
                                {t('measures:omissis_warning:message')}
                            </StyledBrowserEmbeddedContentParagraph>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" onClick={handleCancel}>
                                {t('commons:cancel')}
                            </Button>
                            <Button onClick={handleConfirmView}>{t('measures:omissis_warning:confirm')}</Button>
                        </ModalFooter>
                    </Modal>
                </>
            )}
            {showContent && (
                <StyledBrowserEmbeddedOverlay justify={'center'} align={'center'}>
                    <StyledBrowserEmbeddedContent direction={'column'} align={'flex-end'} gap={4}>
                        <Button onClick={onClose} variant={'ghost'}>
                            <XCloseIcon />
                        </Button>
                        <iframe
                            ref={iframeRef}
                            style={{
                                width: '100%',
                                height: 'calc(100vh - 100px)',
                                border: 'none',
                                backgroundColor: 'white',
                                overflow: 'auto'
                            }}
                            title="embedded-content"
                            sandbox="allow-same-origin allow-scripts"
                        />
                    </StyledBrowserEmbeddedContent>
                </StyledBrowserEmbeddedOverlay>
            )}
        </>,
        document.body // ✅ Il contenuto viene renderizzato nel <body>, fuori dalla tabella
    )
}
